import React, { useState, useEffect } from 'react';
import { getAuthHeaders, BASE_URL } from '../../api/inquiries';

const AiPresetManager = ({ type, value, onChange, disabled }) => {
    const [presets, setPresets] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [filter, setFilter] = useState('my'); // 'my' or 'all'
    const [loading, setLoading] = useState(false);
    const [newTitle, setNewTitle] = useState('');

    const fetchPresets = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${BASE_URL}/api/ai/presets?type=${type}`, { headers: getAuthHeaders() });
            if (res.ok) {
                const data = await res.json();
                setPresets(data.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch presets', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchPresets();
        }
    }, [isOpen]);

    const handleSaveAsPreset = async () => {
        if (!value.trim()) return alert('프롬프트 내용을 먼저 입력해주세요.');
        if (!newTitle.trim()) return alert('프리셋 제목을 입력해주세요.');

        try {
            const res = await fetch(`${BASE_URL}/api/ai/presets`, {
                method: 'POST',
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ type, title: newTitle, promptContent: value, isPublic: true })
            });
            if (res.ok) {
                alert('프리셋이 저장되었습니다.');
                setNewTitle('');
                fetchPresets();
            } else {
                alert('프리셋 저장 실패');
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleDeletePreset = async (id, e) => {
        e.stopPropagation();
        if(!window.confirm('이 프리셋을 삭제하시겠습니까?')) return;
        try {
            const res = await fetch(`${BASE_URL}/api/ai/presets/${id}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });
            if (res.ok) {
                fetchPresets();
            } else {
                const err = await res.json();
                alert(err.message || '삭제에 실패했습니다 (권한 부족 등).');
            }
        } catch (error) {
            console.error(error);
        }
    };

    const myAdminId = (() => {
        try {
            const token = localStorage.getItem('access_token');
            if(!token) return null;
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.sub || payload.id;
        } catch(e) { return null; }
    })();

    const filteredPresets = presets.filter(p => filter === 'all' || p.adminId === myAdminId);

    return (
        <div className="space-y-2 mt-4 p-4 bg-indigo-50/50 border border-indigo-100 rounded-xl relative">
            <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-bold text-indigo-900 block">
                    [선택] 추가 지시사항 (Custom Prompt)
                </label>
                <div className="relative">
                    <button 
                        type="button" 
                        disabled={disabled}
                        onClick={() => setIsOpen(!isOpen)} 
                        className="px-3 py-1.5 bg-white border border-indigo-200 text-indigo-600 text-xs font-bold rounded-lg hover:bg-indigo-50 transition-colors shadow-sm"
                    >
                        {isOpen ? '닫기' : '⭐ 자주 쓰는 프리셋'}
                    </button>
                    
                    {isOpen && (
                        <div className="absolute right-0 top-10 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-4">
                            <div className="flex gap-2 mb-4 border-b border-gray-100 pb-2">
                                <button type="button" onClick={()=>setFilter('my')} className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-colors ${filter === 'my' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>내 프롬프트</button>
                                <button type="button" onClick={()=>setFilter('all')} className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-colors ${filter === 'all' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>모든 공유 프롬프트</button>
                            </div>
                            
                            <div className="max-h-60 overflow-y-auto space-y-2 mb-4 pr-1 snap-y">
                                {loading && <div className="text-xs text-center text-gray-400 py-4">로딩 중...</div>}
                                {!loading && filteredPresets.length === 0 && <div className="text-xs text-center text-gray-400 py-4">저장된 프리셋이 없습니다.</div>}
                                {!loading && filteredPresets.map(p => (
                                    <div key={p.id} onClick={() => onChange(p.promptContent)} className="group p-3 border border-gray-100 rounded-lg cursor-pointer hover:border-indigo-300 hover:bg-indigo-50 transition-all text-left">
                                        <div className="flex justify-between items-start">
                                            <span className="text-xs font-bold text-gray-800">{p.title}</span>
                                            {p.adminId === myAdminId && (
                                                <button type="button" onClick={(e) => handleDeletePreset(p.id, e)} className="text-red-400 opacity-0 group-hover:opacity-100 hover:text-red-600 text-xs px-1">✕</button>
                                            )}
                                        </div>
                                        <p className="text-[10px] text-gray-500 mt-1 line-clamp-2">{p.promptContent}</p>
                                    </div>
                                ))}
                            </div>
                            
                            <div className="pt-3 border-t border-gray-100 flex gap-2">
                                <input 
                                    type="text" 
                                    placeholder="저장할 제목 입력" 
                                    className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-indigo-400"
                                    value={newTitle}
                                    onChange={e => setNewTitle(e.target.value)}
                                />
                                <button type="button" onClick={handleSaveAsPreset} className="bg-indigo-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-indigo-700 whitespace-nowrap">현재 내용 저장</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <textarea 
                value={value} 
                onChange={e => onChange(e.target.value)} 
                disabled={disabled}
                placeholder="AI가 초안을 작성할 때 반드시 지켰으면 하는 톤앤매너나 강조점을 자유롭게 적어주세요. (예: 공공기관 보도자료처럼 딱딱하고 신뢰감 있게 구성해줘.)" 
                rows={2} 
                className="w-full px-4 py-3 border border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none shadow-sm text-sm text-gray-800 font-medium disabled:opacity-50 transition-colors"
            />
        </div>
    );
};

export default AiPresetManager;
